// src/utils/generateInvoicePdf.js
import jsPDF from 'jspdf';

function formatDate(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('fi-FI', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export function generateInvoicePdf({ courierInfo, clientCompany, clientYNumber, deliveries, invoiceNumber, alvRate }) {
  const pdf  = new jsPDF();
  const now  = new Date();
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const M     = 14;

  // ── Header ────────────────────────────────────────────────────────────────

  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.text('LASKU', pageW - M, 20, { align: 'right' });

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Laskunumero: ${invoiceNumber}`,              pageW - M, 28, { align: 'right' });
  pdf.text(`Päivämäärä: ${now.toLocaleDateString('fi-FI')}`, pageW - M, 34, { align: 'right' });

  // ── Sender ────────────────────────────────────────────────────────────────

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Lähettäjä', M, 20);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  let senderY = 27;
  pdf.text(courierInfo.companyName || '—', M, senderY); senderY += 6;
  pdf.text(`Y-tunnus: ${courierInfo.yNumber || '—'}`,   M, senderY); senderY += 6;
  if (courierInfo.iban) {
    pdf.text(`IBAN: ${courierInfo.iban}`, M, senderY);
  }

  // ── Recipient ────────────────────────────────────────────────────────────

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Vastaanottaja', M, 56);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.text(clientCompany || '—',              M, 63);
  pdf.text(`Y-tunnus: ${clientYNumber || '—'}`, M, 69);

  // ── Divider ───────────────────────────────────────────────────────────────

  pdf.setDrawColor(200, 200, 200);
  pdf.line(M, 78, pageW - M, 78);

  // ── Table ─────────────────────────────────────────────────────────────────

  const cols    = [46, 24, 50, 50, 22];
  const headers = ['Toimitusnro', 'Pvm', 'Lähtöpaikka', 'Määränpää', 'Netto €'];
  const rowH    = 8;
  const totalColW = cols.reduce((a, b) => a + b, 0);

  let y = 84;

  // Header row
  pdf.setFillColor(37, 99, 235);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  let x = M;
  cols.forEach((w, i) => {
    pdf.rect(x, y, w, rowH, 'F');
    pdf.text(headers[i], x + 2, y + 5.5);
    x += w;
  });
  y += rowH;

  // Data rows — with page-break guard
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');

  // Totals block height: divider + 3 rows × 7 + padding ≈ 40
  const TOTALS_HEIGHT = 44;

  deliveries.forEach((d, idx) => {
    // Page break: leave room for totals on the last row
    if (y + rowH + (idx === deliveries.length - 1 ? TOTALS_HEIGHT : 0) > pageH - M) {
      pdf.addPage();
      y = 20;
    }

    if (idx % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(M, y, totalColW, rowH, 'F');
    }

    const cells = [
      (d.deliveryId || d.id || '').substring(0, 18),
      formatDate(d.createdAt),
      (d.origin      || '—').substring(0, 22),
      (d.destination || '—').substring(0, 22),
      Number(d.reward || 0).toFixed(2),
    ];

    x = M;
    pdf.setFontSize(8);
    cells.forEach((text, i) => {
      pdf.text(String(text), x + 2, y + 5.5);
      x += cols[i];
    });
    y += rowH;
  });

  // ── Totals block ─────────────────────────────────────────────────────────

  // Ensure the totals never collide with the last row
  if (y + TOTALS_HEIGHT > pageH - M) {
    pdf.addPage();
    y = 20;
  }

  const netTotal   = deliveries.reduce((sum, d) => sum + (Number(d.reward) || 0), 0);
  const alvAmount  = netTotal * (alvRate / 100);
  const grossTotal = netTotal + alvAmount;

  y += 8; // breathing room between table and totals
  pdf.setDrawColor(200, 200, 200);
  pdf.line(M, y, pageW - M, y);
  y += 8;

  const labelX = pageW - 80;
  const valueX = pageW - M;

  const addTotalRow = (label, value, bold = false) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(9);
    pdf.text(label, labelX, y);
    pdf.text(value, valueX, y, { align: 'right' });
    y += 7;
  };

  addTotalRow('Veroton yhteensä:',    `${netTotal.toFixed(2)} €`);
  addTotalRow(`ALV ${alvRate} %:`,    `${alvAmount.toFixed(2)} €`);

  pdf.setDrawColor(200, 200, 200);
  pdf.line(labelX, y - 3, pageW - M, y - 3);

  addTotalRow('Yhteensä (sis. ALV):', `${grossTotal.toFixed(2)} €`, true);

  pdf.save(`lasku-${invoiceNumber}.pdf`);
}